import { z } from 'zod';
export const CONTACT_STATUS = [
    'active',
    'unsubscribed',
    'bounced',
    'complained',
    'suppressed',
];
export const LIFECYCLE_STAGE = ['subscriber', 'lead', 'mql', 'sql', 'customer', 'churned'];
export const CONSENT_STATUS = ['unknown', 'pending', 'confirmed', 'withdrawn'];
export const contact = z.object({
    id: z.string().uuid(),
    projectId: z.string().uuid(),
    email: z.string().email(),
    firstName: z.string().nullable(),
    lastName: z.string().nullable(),
    phone: z.string().nullable(),
    companyId: z.string().uuid().nullable(),
    companyName: z.string().nullable().optional(),
    status: z.enum(CONTACT_STATUS),
    source: z.string().nullable(),
    country: z.string().nullable(),
    city: z.string().nullable(),
    leadScore: z.number().int(),
    lifecycleStage: z.enum(LIFECYCLE_STAGE),
    consentStatus: z.enum(CONSENT_STATUS),
    consentAt: z.string().datetime().nullable(),
    lastActivityAt: z.string().datetime().nullable(),
    custom: z.record(z.unknown()),
    tags: z
        .array(z.object({ id: z.string().uuid(), name: z.string(), color: z.string() }))
        .optional(),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
});
export const createContactRequest = z.object({
    email: z.string().min(1, 'E-Mail-Adresse fehlt').email('Keine gueltige E-Mail-Adresse'),
    firstName: z.string().max(120).optional().nullable(),
    lastName: z.string().max(120).optional().nullable(),
    phone: z.string().max(60).optional().nullable(),
    companyId: z.string().uuid().optional().nullable(),
    country: z.string().max(2).optional().nullable(),
    city: z.string().max(120).optional().nullable(),
    source: z.string().max(120).optional().nullable(),
    lifecycleStage: z.enum(LIFECYCLE_STAGE).optional(),
    consentStatus: z.enum(CONSENT_STATUS).optional(),
    custom: z.record(z.unknown()).optional(),
    listIds: z.array(z.string().uuid()).optional(),
    tagIds: z.array(z.string().uuid()).optional(),
});
export const updateContactRequest = createContactRequest.partial().extend({
    status: z.enum(CONTACT_STATUS).optional(),
    ownerUserId: z.string().uuid().nullable().optional(),
});
/** Sortier- und Filterparameter der Kontaktliste. */
export const contactQuery = z.object({
    search: z.string().trim().max(200).optional(),
    status: z.enum(CONTACT_STATUS).optional(),
    listId: z.string().uuid().optional(),
    tagId: z.string().uuid().optional(),
    lifecycleStage: z.enum(LIFECYCLE_STAGE).optional(),
    sort: z.enum(['createdAt', 'lastActivityAt', 'leadScore', 'email']).default('createdAt'),
    direction: z.enum(['asc', 'desc']).default('desc'),
    limit: z.coerce.number().int().min(1).max(200).default(50),
    cursor: z.string().optional(),
});
export const contactPage = z.object({
    items: z.array(contact),
    total: z.number().int(),
    nextCursor: z.string().nullable(),
});
// --- Listen -----------------------------------------------------------------
export const list = z.object({
    id: z.string().uuid(),
    name: z.string(),
    description: z.string().nullable(),
    type: z.enum(['static', 'dynamic']),
    contactCount: z.number().int(),
    createdAt: z.string().datetime(),
});
export const createListRequest = z.object({
    name: z.string().trim().min(1, 'Name fehlt').max(120),
    description: z.string().max(500).optional().nullable(),
});
export const updateListRequest = createListRequest.partial();
// --- Massenbearbeitung -------------------------------------------------------
/**
 * Ausgewaehlte Kontakte, ueber ihre Kennungen benannt.
 *
 * Bewusst nicht "alles, was auf den Filter passt": ein Filter, der sich
 * zwischen Ansehen und Ausfuehren aendert — durch einen Import, einen Bounce,
 * eine zweite Sitzung — trifft am Ende andere Zeilen als die, die jemand
 * gesehen und bestaetigt hat. Bei einer Loeschung ist das der Unterschied
 * zwischen 12 und 12.000 Kontakten.
 *
 * 1000 je Aufruf ist die Grenze. Mehr traegt die Oberflaeche ohnehin nicht in
 * einer Auswahl, und der Aufruf bleibt in einer Transaktion beherrschbar.
 */
const bulkIds = z
    .array(z.string().uuid())
    .min(1, 'Es ist kein Kontakt ausgewählt.')
    .max(1000, 'Höchstens 1000 Kontakte auf einmal.');
export const bulkListRequest = z.object({
    ids: bulkIds,
    listId: z.string().uuid(),
    /**
     * add     — zur Liste hinzufuegen, bestehende Zugehoerigkeiten bleiben
     * remove  — nur aus dieser Liste nehmen
     * move    — aus allen Listen nehmen und in diese eine setzen
     */
    action: z.enum(['add', 'remove', 'move']),
});
export const bulkDeleteRequest = z.object({ ids: bulkIds });
export const bulkResult = z.object({
    /** Wie viele Kontakte die Aktion tatsaechlich betroffen hat. */
    affected: z.number().int(),
    /**
     * Angefragt, aber nicht betroffen — etwa weil der Kontakt inzwischen
     * geloescht wurde oder gar nicht zu diesem Projekt gehoert. Wird gemeldet
     * statt verschwiegen.
     */
    skipped: z.number().int(),
});
// --- Tags -------------------------------------------------------------------
export const createTagRequest = z.object({
    name: z.string().trim().min(1, 'Name fehlt').max(60),
    color: z.string().max(20).default('gray'),
});
// --- Unterdrueckung ---------------------------------------------------------
export const createSuppressionRequest = z.object({
    scope: z.enum(['email', 'domain', 'pattern']),
    value: z.string().trim().min(1, 'Wert fehlt').max(320),
    reason: z.enum(['unsubscribe', 'bounce', 'complaint', 'manual', 'import', 'no_interest']).default('manual'),
    note: z.string().max(500).optional().nullable(),
});
/**
 * Eine bestehende Sperrliste uebernehmen.
 *
 * Die Regelart je Zeile erkennt parseSuppressionList() im Kern — hier steht
 * nur, was hereindarf. 4 MB entsprechen grob 200.000 Adressen; groessere
 * Bestaende gehoeren in mehrere Durchgaenge, damit ein Fehlschlag nicht die
 * ganze Uebernahme kostet.
 */
export const importSuppressionsRequest = z.object({
    text: z.string().min(1, 'Es wurde nichts eingefügt.').max(4_000_000),
    reason: z.enum(['unsubscribe', 'bounce', 'complaint', 'manual', 'import', 'no_interest']).default('import'),
    note: z.string().max(500).optional().nullable(),
});
// --- Import -----------------------------------------------------------------
export const CONTACT_FIELDS = [
    'email',
    'firstName',
    'lastName',
    'phone',
    'company',
    'street',
    'postalCode',
    'country',
    'city',
    'website',
    'googleMapsUrl',
    'googlePlaceId',
    'rating',
    'category',
    'source',
    'role',
    'ratingCount',
    'openingHours',
    'googleProfileUrl',
    'contactKind',
    'legalBasis',
    'industry',
    'size',
    'tag',
    'ignore',
];
export const importPreview = z.object({
    headers: z.array(z.string()),
    sampleRows: z.array(z.array(z.string())),
    totalRowsEstimate: z.number().int(),
    mapping: z.array(z.object({
        column: z.string(),
        field: z.enum(CONTACT_FIELDS),
        inferred: z.boolean(),
    })),
});
/**
 * Eingefuegte Tabelle aus der Zwischenablage.
 *
 * Excel, Google Sheets, Proton und LibreOffice legen beim Kopieren
 * tabulatorgetrennten Text ab — dasselbe Format, das die Trennzeichen-
 * erkennung ohnehin beherrscht. Es braucht deshalb keinen eigenen Leser,
 * nur einen eigenen Weg herein.
 *
 * 8 MB entsprechen grob 100.000 Zeilen. Wer mehr hat, laedt eine Datei hoch:
 * dieser Weg haelt den Inhalt zwischenzeitlich vollstaendig im Speicher.
 */
export const importTextRequest = z.object({
    text: z.string().min(1, 'Es wurde nichts eingefügt.').max(8_000_000),
    filename: z.string().max(120).optional(),
});
/** Import von einer Adresse. Was der Server dabei abrufen darf, regelt remote-fetch.ts. */
export const importUrlRequest = z.object({
    url: z.string().trim().url('Das ist keine gültige Adresse.').max(2048),
});
export const importRunRequest = z.object({
    listId: z.string().uuid().optional().nullable(),
    mapping: z.array(z.object({ column: z.string(), field: z.enum(CONTACT_FIELDS) })),
    /** Bestehende Kontakte aktualisieren statt ueberspringen. */
    updateExisting: z.boolean().default(true),
    /** Einwilligung, die fuer alle importierten Zeilen dokumentiert wird. */
    consentStatus: z.enum(CONSENT_STATUS).default('unknown'),
    consentSource: z.string().max(200).optional().nullable(),
});
export const importResult = z.object({
    jobId: z.string().uuid(),
    totalRows: z.number().int(),
    created: z.number().int(),
    updated: z.number().int(),
    skipped: z.number().int(),
    issues: z.array(z.object({ row: z.number().int(), email: z.string().optional(), reason: z.string() })),
});
//# sourceMappingURL=contacts.js.map